import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import { useApp } from '../../context/AppContext';
import '../../styles/map.css';

export default function FleetMap() {
  const { LIVE, lessorId } = useApp();
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const acRef = useRef([]);
  const projRef = useRef(null);
  const [tooltip, setTooltip] = useState({ show: false, html: '', x: 0, y: 0 });
  const intervalRef = useRef(null);

  const ops = [...new Set(LIVE.map(a => a.op))];
  const opColors = {};
  LIVE.forEach(a => { opColors[a.op] = a.col; });

  useEffect(() => {
    acRef.current = LIVE.map(a => ({ ...a }));

    const wrap = wrapRef.current;
    const W = wrap?.clientWidth || 900;
    const H = 380;

    const svg = d3.select(svgRef.current)
      .attr('viewBox', `0 0 ${W} ${H}`);

    svg.selectAll('*').remove();
    clearInterval(intervalRef.current);

    svg.append('rect').attr('width', W).attr('height', H).attr('fill', '#dce8f4');

    const proj = d3.geoNaturalEarth1().scale(W / 6.5).translate([W / 2, H / 2]);
    projRef.current = proj;
    const path = d3.geoPath().projection(proj);

    const grat = d3.geoGraticule().step([30, 30]);
    svg.append('path')
      .datum(grat())
      .attr('fill', 'none')
      .attr('stroke', '#c0d4e8')
      .attr('stroke-width', '0.4')
      .attr('d', path);

    fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json')
      .then(r => r.json())
      .then(world => {
        svg.insert('g', '#ac-layer')
          .selectAll('path')
          .data(topojson.feature(world, world.objects.countries).features)
          .enter().append('path')
          .attr('d', path)
          .attr('fill', '#c6d6bc')
          .attr('stroke', '#afc0a5')
          .attr('stroke-width', '0.5');

        svg.insert('path', '#ac-layer')
          .datum(topojson.mesh(world, world.objects.countries, (a, b) => a !== b))
          .attr('fill', 'none')
          .attr('stroke', '#a5b89a')
          .attr('stroke-width', '0.3')
          .attr('d', path);

        plotAircraft(svg, proj, W, H);
      })
      .catch(() => plotAircraft(svg, proj, W, H));

    intervalRef.current = setInterval(() => {
      acRef.current.forEach((ac, i) => {
        if (ac.gnd) return;
        const rad = ac.hdg * Math.PI / 180;
        ac.lat += Math.cos(rad) * 0.2 * (0.7 + Math.random() * 0.6);
        ac.lng += Math.sin(rad) * 0.26 * (0.7 + Math.random() * 0.6);
        const [x, y] = proj([ac.lng, ac.lat]);
        const g = svgRef.current?.querySelector(`#ac${i}`);
        if (g) g.setAttribute('transform', `translate(${x.toFixed(1)},${y.toFixed(1)}) rotate(${ac.hdg})`);
      });
    }, 3000);

    return () => clearInterval(intervalRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessorId]);

  function plotAircraft(svg, proj, W, H) {
    const old = svgRef.current?.querySelector('#ac-layer');
    if (old) old.remove();

    const ns = 'http://www.w3.org/2000/svg';
    const layer = document.createElementNS(ns, 'g');
    layer.id = 'ac-layer';
    svgRef.current.appendChild(layer);

    acRef.current.forEach((ac, i) => {
      const [x, y] = proj([ac.lng, ac.lat]);
      const g = document.createElementNS(ns, 'g');
      g.id = `ac${i}`;
      g.setAttribute('class', 'acm');
      g.setAttribute('transform', `translate(${x.toFixed(1)},${y.toFixed(1)}) rotate(${ac.hdg})`);

      if (!ac.gnd) {
        const halo = document.createElementNS(ns, 'circle');
        halo.setAttribute('r', '8');
        halo.setAttribute('fill', ac.col);
        halo.setAttribute('opacity', '0.18');
        halo.classList.add('ac-pulse');
        g.appendChild(halo);
      }

      const plane = document.createElementNS(ns, 'path');
      plane.setAttribute('d', 'M0,-7 L1.4,-2.5 L5,0.5 L1.8,1.2 L0.7,4 L0,3.6 L-0.7,4 L-1.8,1.2 L-5,0.5 L-1.4,-2.5 Z');
      plane.setAttribute('fill', ac.col);
      plane.setAttribute('stroke', 'rgba(255,255,255,0.85)');
      plane.setAttribute('stroke-width', '0.8');
      plane.setAttribute('transform', `scale(${ac.gnd ? 0.5 : 0.65})`);
      g.appendChild(plane);

      const hit = document.createElementNS(ns, 'circle');
      hit.setAttribute('r', '11');
      hit.setAttribute('fill', 'transparent');
      g.appendChild(hit);

      g.addEventListener('mouseenter', () => {
        const wr = wrapRef.current;
        const wW = wr?.clientWidth || 900;
        const scaleX = wW / 900;
        const scaleY = (wr?.clientHeight || 380) / 380;
        setTooltip({
          show: true,
          html: `<strong>${ac.reg}</strong><br><span style="color:#8faabb">${ac.op}</span><br>${ac.v}<br>${ac.gnd ? '<span style="color:#fbbf24">● On Ground</span>' : '<span style="color:#6ee7b7">● Airborne</span>'}`,
          x: Math.min(x * scaleX + 14, wW - 175),
          y: Math.max(y * scaleY - 55, 5),
        });
      });
      g.addEventListener('mouseleave', () => setTooltip(t => ({ ...t, show: false })));

      layer.appendChild(g);
    });
  }

  return (
    <div>
      <div id="mapwrap" ref={wrapRef}>
        <svg id="mapsvg" ref={svgRef} viewBox="0 0 900 380" preserveAspectRatio="xMidYMid meet" />
        <div
          className={`mtt${tooltip.show ? ' show' : ''}`}
          style={{ left: tooltip.x, top: tooltip.y }}
          dangerouslySetInnerHTML={{ __html: tooltip.html }}
        />
      </div>
      <div className="mleg">
        {ops.map(op => (
          <div className="mli" key={op}>
            <div className="mld" style={{ background: opColors[op] }} />
            {op}
          </div>
        ))}
      </div>
    </div>
  );
}
